/**
 * Data layer for `referral-partner-program-earn-by-referring-accounts`. Thin typed fetch wrappers over /api/v1/referral-partner-program-earn-by-referring-accounts.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface ReferralPartnerProgramEarnByReferringAccountsRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListReferralPartnerProgramEarnByReferringAccountsResponse {
	count: number;
	items: ReferralPartnerProgramEarnByReferringAccountsRow[];
}

export interface CreateReferralPartnerProgramEarnByReferringAccountsBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/referral-partner-program-earn-by-referring-accounts";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListReferralPartnerProgramEarnByReferringAccountsResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListReferralPartnerProgramEarnByReferringAccountsResponse>(res);
}

export async function create(body: CreateReferralPartnerProgramEarnByReferringAccountsBody): Promise<ReferralPartnerProgramEarnByReferringAccountsRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<ReferralPartnerProgramEarnByReferringAccountsRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const referralPartnerProgramEarnByReferringAccountsQueryKeys = {
	all: ["referral-partner-program-earn-by-referring-accounts"] as const,
	list: () => [...referralPartnerProgramEarnByReferringAccountsQueryKeys.all] as const,
	detail: (id: string) => [...referralPartnerProgramEarnByReferringAccountsQueryKeys.all, id] as const,
};
